import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, Save, Globe, GlobeLock, Image as ImageIcon, Eye, EyeOff, X } from 'lucide-react';
import { AdminLayout } from '../../components/admin';
import { Button, Input } from '../../components/ui';
import { toast } from '../../components/ui/Toaster';
import { blogService, BlogPost, BlogPostInput } from '../../services/blogService';
import { getUploadUrl } from '../../utils/url';
import { getPublicBlogUrl } from '../../utils/blogUrls';

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

const emptyForm = {
  title: '',
  slug: '',
  excerpt: '',
  metaDescription: '',
  content: '',
  coverImage: '' as string | null,
};

const BlogEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [post, setPost] = useState<BlogPost | null>(null);
  const [slugEdited, setSlugEdited] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => {
    if (isNew) return;
    let active = true;
    setLoading(true);
    blogService
      .getAdmin(parseInt(id!, 10))
      .then((data) => {
        if (!active) return;
        setPost(data);
        setForm({
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt || '',
          metaDescription: data.metaDescription || '',
          content: data.content,
          coverImage: data.coverImage || '',
        });
        setSlugEdited(true);
      })
      .catch((error) => {
        console.error('Error loading blog post:', error);
        toast.error('Fout', 'Kon blogbericht niet laden');
        navigate('/admin/blog');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  // Auto-derive slug from title until the user edits it manually.
  const effectiveSlug = useMemo(() => {
    if (slugEdited) return form.slug;
    return slugify(form.title);
  }, [form.title, form.slug, slugEdited]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploading(true);
      const path = await blogService.uploadImage(file);
      setForm((f) => ({ ...f, coverImage: path }));
      toast.success('Geüpload', 'Omslagafbeelding toegevoegd');
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast.error('Fout', error.message || 'Uploaden mislukt');
    } finally {
      setUploading(false);
    }
  };

  const buildPayload = (publish: boolean | null): Partial<BlogPostInput> => {
    const payload: Partial<BlogPostInput> = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim() || null,
      metaDescription: form.metaDescription.trim() || null,
      content: form.content,
      coverImage: form.coverImage || null,
    };
    const slug = effectiveSlug.trim();
    if (slug) {
      payload.slug = slug;
    }
    if (publish !== null) {
      payload.isPublished = publish;
    }
    return payload;
  };

  const validate = (): boolean => {
    if (!form.title.trim()) {
      toast.error('Titel ontbreekt', 'Geef het bericht een titel');
      return false;
    }
    if (!form.content.trim()) {
      toast.error('Inhoud ontbreekt', 'Schrijf eerst wat inhoud');
      return false;
    }
    return true;
  };

  const handleSave = async (publish: boolean | null) => {
    if (!validate()) return;
    try {
      setSaving(true);
      let saved: BlogPost;
      if (isNew) {
        saved = await blogService.create(buildPayload(publish ?? false) as BlogPostInput);
      } else {
        saved = await blogService.update(post!.id, buildPayload(publish));
      }
      setPost(saved);
      setForm((f) => ({ ...f, slug: saved.slug }));
      setSlugEdited(true);

      const msg =
        publish === true
          ? 'Opgeslagen en gepubliceerd'
          : publish === false
          ? 'Opgeslagen als concept'
          : 'Opgeslagen';
      toast.success('Succes', msg);

      if (isNew) {
        navigate(`/admin/blog/${saved.id}`, { replace: true });
      }
    } catch (error: any) {
      console.error('Error saving blog post:', error);
      toast.error('Fout', error.response?.data?.message || error.message || 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Blog editor">
        <div className="flex items-center justify-center h-64 text-fg-muted">Laden...</div>
      </AdminLayout>
    );
  }

  const coverUrl = getUploadUrl(form.coverImage);
  const published = post?.isPublished ?? false;

  return (
    <AdminLayout
      title={isNew ? 'Nieuw blogbericht' : 'Blogbericht bewerken'}
      subtitle={isNew ? 'Schrijf een nieuw bericht' : `/blog/${post?.slug || ''}`}
    >
      <div className="space-y-6">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" onClick={() => navigate('/admin/blog')} className="flex items-center">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Terug
          </Button>

          <div className="flex flex-wrap items-center gap-3">
            {!isNew && published && (
              <Button
                variant="outline"
                onClick={() => window.open(getPublicBlogUrl(post!.slug), '_blank')}
                className="flex items-center"
              >
                <Eye className="h-4 w-4 mr-2" />
                Bekijk live
              </Button>
            )}

            {isNew ? (
              <>
                <Button variant="secondary" onClick={() => handleSave(false)} loading={saving}>
                  Opslaan als concept
                </Button>
                <Button onClick={() => handleSave(true)} loading={saving} className="flex items-center">
                  <Globe className="h-4 w-4 mr-2" />
                  Publiceren
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={() => handleSave(null)} loading={saving} className="flex items-center">
                  <Save className="h-4 w-4 mr-2" />
                  Opslaan
                </Button>
                <Button
                  variant={published ? 'outline' : 'primary'}
                  onClick={() => handleSave(!published)}
                  loading={saving}
                  className="flex items-center"
                >
                  {published ? (
                    <>
                      <GlobeLock className="h-4 w-4 mr-2" />
                      Depubliceren
                    </>
                  ) : (
                    <>
                      <Globe className="h-4 w-4 mr-2" />
                      Publiceren
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Status */}
        {!isNew && (
          <div className="sf-card p-4 flex items-center justify-between">
            <div>
              <p className="sf-label-mono">Status</p>
              <p className="mt-1">
                {published ? (
                  <span className="sf-badge-lime">Gepubliceerd</span>
                ) : (
                  <span className="sf-badge-cyan">Concept</span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="sf-card p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">Titel</label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Titel van het blogbericht"
              maxLength={200}
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">URL-slug</label>
            <div className="flex items-center gap-2">
              <span className="text-fg-muted text-sm font-mono">/blog/</span>
              <Input
                value={effectiveSlug}
                onChange={(e) => {
                  setSlugEdited(true);
                  setForm({ ...form, slug: slugify(e.target.value) });
                }}
                placeholder="url-slug"
                maxLength={255}
              />
            </div>
            <p className="font-mono text-[11px] text-fg-meta mt-1">
              Wordt automatisch afgeleid van de titel. Alleen kleine letters, cijfers en koppeltekens.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">
              Samenvatting (excerpt)
            </label>
            <textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              placeholder="Korte samenvatting die op de bloglijst verschijnt"
              className="sf-input"
              rows={2}
              maxLength={300}
            />
            <p className="font-mono text-[11px] text-fg-meta mt-1">{form.excerpt.length}/300</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">
              Meta description (SEO)
            </label>
            <Input
              value={form.metaDescription}
              onChange={(e) => setForm({ ...form, metaDescription: e.target.value })}
              placeholder="Beschrijving voor zoekmachines (max 160 tekens)"
              maxLength={160}
            />
            <p className="font-mono text-[11px] text-fg-meta mt-1">
              {form.metaDescription.length}/160
            </p>
          </div>

          {/* Cover image */}
          <div>
            <label className="block text-sm font-semibold text-fg-soft mb-2">Omslagafbeelding</label>
            {coverUrl ? (
              <div className="relative inline-block">
                <img
                  src={coverUrl}
                  alt="Omslag"
                  className="max-h-48 rounded-card border border-white/[.08]"
                />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, coverImage: '' })}
                  className="absolute -top-2 -right-2 bg-canvas border border-white/[.15] rounded-full p-1 text-fg-muted hover:text-neon-pink-soft"
                  aria-label="Afbeelding verwijderen"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 cursor-pointer sf-card px-4 py-3 text-sm text-fg-body hover:bg-white/[.05] transition-colors">
                <ImageIcon className="h-4 w-4 text-neon-cyan" />
                {uploading ? 'Uploaden...' : 'Afbeelding uploaden'}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
              </label>
            )}
          </div>

          {/* Content */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-fg-soft">
                Inhoud (Markdown)
              </label>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="inline-flex items-center gap-1.5 text-xs text-fg-muted hover:text-fg-bright transition-colors"
              >
                {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {showPreview ? 'Verberg voorbeeld' : 'Toon voorbeeld'}
              </button>
            </div>

            {showPreview ? (
              <div
                className="sf-card p-6 min-h-[300px] text-fg-body leading-relaxed
                  [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-fg-bright [&_h1]:mt-6 [&_h1]:mb-3
                  [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-fg-bright [&_h2]:mt-6 [&_h2]:mb-2
                  [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-fg-bright [&_h3]:mt-4 [&_h3]:mb-2
                  [&_p]:mb-4 [&_a]:text-neon-cyan [&_a]:underline [&_strong]:text-fg-soft
                  [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
                  [&_blockquote]:border-l-2 [&_blockquote]:border-neon-cyan [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-fg-muted"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {form.content || '_Niets om te tonen_'}
                </ReactMarkdown>
              </div>
            ) : (
              <textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Schrijf je bericht in Markdown. Gebruik ## voor koppen, **vet**, - voor lijsten, etc."
                className="sf-input font-mono text-sm"
                rows={22}
              />
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BlogEditorPage;
